/**
 * 課題文フォームエディタ
 * カテゴリ ON/OFF → そのカテゴリ配下にタスク複数（各タスクが担当Lvのサブセットを持つ）
 * タスクはカテゴリ配下で評価項目数まで追加可能（V3.1の可変levelKeyに対応）
 * 1つのLvは同一カテゴリ内で1タスクにのみ所属（排他）
 */

import { useCallback, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import type { CategoryTableData } from './ConditionCategoryTable';
import {
  normalizeConditionCategories,
  type LevelKind,
} from '../../lib/conditionTableNormalizer';

interface InstructionTask {
  no: number;
  title: string;
  details?: string[];
  related_conditions?: string[];
  levels?: string[]; // このタスクが担当する Lv のサブセット
}

interface InstructionFormData {
  summary?: string;
  tasks?: InstructionTask[];
  target_categories?: string[];
  target_category_levels?: Record<string, string[]>;
}

interface InstructionFormEditorProps {
  data: InstructionFormData;
  onChange: (data: InstructionFormData) => void;
  disabled?: boolean;
  /** 紐づく健康心理条件の categoryTable（Lvボタンホバーで値を表示するために使う） */
  categoryTable?: CategoryTableData | null;
  /** V3.1 の可変カテゴリ・可変Lvを含む condition_data */
  conditionData?: Record<string, unknown> | null;
}

interface LevelOption {
  key: string;
  label: string;
  tooltip: string;
  kind?: LevelKind;
}

interface CategoryOption {
  id: string;
  sourceId: string;
  label: string;
  levels: LevelOption[];
}

// 条件データを読めないときの予備。番号の意味は CSV の版で変わる（旧C6＝喫煙、新C6＝マウスウォッシュ）ため、
// 旧ルーブリックの名前で補わず番号のまま出す。新しい CSV の範囲（C1〜C30）に合わせる。
const FALLBACK_CATEGORY_OPTIONS: CategoryOption[] = Array.from({ length: 30 }, (_, index) => ({
  id: `C${index + 1}`,
  label: `C${index + 1}`,
})).map((category) => ({
  ...category,
  sourceId: category.id,
  levels: ['Lv1', 'Lv2', 'Lv3', 'Lv4', 'Lv5', 'Lv6'].map((lv) => ({
    key: lv,
    label: lv,
    tooltip: '',
  })),
}));

const LEGACY_LV_OPTIONS = ['Lv1', 'Lv2', 'Lv3', 'Lv4', 'Lv5', 'Lv6'];

function canonicalTargetCategoryId(categoryId: string): string {
  const normalized = categoryId.trim().toUpperCase();
  const srcMatch = normalized.match(/^SRC[_-]?(C\d{1,2})$/);
  if (srcMatch?.[1]) return srcMatch[1];
  return normalized;
}

function levelOptionLabel(levelKey: string, label: string): string {
  if (levelKey === 'proposal') return '歯科衛生士提案';
  if (levelKey === 'acceptance_intent') return '患者受容・意思';
  if (label && label !== levelKey) return label;
  return levelKey;
}

function buildDynamicCategoryOptions(conditionData?: Record<string, unknown> | null): CategoryOption[] {
  const categories = normalizeConditionCategories(conditionData);
  if (categories.length === 0) return FALLBACK_CATEGORY_OPTIONS;

  return categories.map((category) => {
    const levels = new Map<string, LevelOption>();
    for (const item of category.items) {
      const current = levels.get(item.level);
      const itemLabel = levelOptionLabel(item.level, item.label || item.sourceHeader || item.columnKey);
      const line = `【${itemLabel}】${item.text}`;
      if (current) {
        levels.set(item.level, {
          ...current,
          tooltip: [current.tooltip, line].filter(Boolean).join('\n\n'),
        });
        continue;
      }
      levels.set(item.level, {
        key: item.level,
        label: itemLabel,
        tooltip: line,
        kind: item.kind,
      });
    }

    return {
      id: canonicalTargetCategoryId(category.categoryId),
      sourceId: category.categoryId,
      label: category.categoryName,
      levels: Array.from(levels.values()),
    };
  });
}

function taskCategoryId(task: InstructionTask): string {
  return canonicalTargetCategoryId((task.related_conditions ?? [])[0] ?? '');
}

/** 表示順（idx+1）に no を再採番して整合を取る */
function renumberTasks(tasks: InstructionTask[]): InstructionTask[] {
  return tasks.map((t, i) => ({ ...t, no: i + 1 }));
}

/** カテゴリ配下のタスクのインデックス列を返す */
function indicesByCategory(tasks: InstructionTask[], catId: string): number[] {
  const result: number[] = [];
  tasks.forEach((t, i) => {
    if (taskCategoryId(t) === catId) result.push(i);
  });
  return result;
}

/** tasks 全体から target_category_levels を再構築（保存時の整合用） */
function rebuildTargetCategoryLevels(
  tasks: InstructionTask[],
  categoryOptions: CategoryOption[],
  previousLevels?: Record<string, string[]>,
): Record<string, string[]> {
  const map: Record<string, Set<string>> = {};
  tasks.forEach((t) => {
    const catId = taskCategoryId(t);
    if (!catId) return;
    if (!map[catId]) map[catId] = new Set();
    (t.levels ?? []).forEach((lv) => map[catId].add(lv));
  });
  const out: Record<string, string[]> = {};
  Object.entries(map).forEach(([catId, set]) => {
    if (set.size === 0) return;
    const levelOrder = categoryOptions.find((category) => category.id === catId)?.levels.map((level) => level.key)
      ?? LEGACY_LV_OPTIONS;
    const ordered = levelOrder.filter((level) => set.has(level));
    const extras = Array.from(set).filter((level) => !ordered.includes(level));
    out[catId] = [...ordered, ...extras];
  });

  for (const category of categoryOptions) {
    if (out[category.id]) continue;
    const previous = getTargetLevelsForCategory(previousLevels, category);
    const hasTaskWithoutLevels = tasks.some((task) => (
      taskCategoryId(task) === category.id && !task.levels
    ));
    if (previous && hasTaskWithoutLevels) {
      out[category.id] = previous;
    }
  }

  return out;
}

function getTargetLevelsForCategory(
  targetCategoryLevels: Record<string, string[]> | undefined,
  category: CategoryOption,
): string[] | undefined {
  if (!targetCategoryLevels) return undefined;
  const stored = targetCategoryLevels[category.id] ??
    targetCategoryLevels[category.sourceId] ??
    targetCategoryLevels[canonicalTargetCategoryId(category.sourceId)];
  if (!stored) return undefined;

  const expanded = new Set<string>();
  const availableKeys = new Set(category.levels.map((level) => level.key));
  for (const level of stored) {
    if (availableKeys.has(level)) {
      expanded.add(level);
      continue;
    }
    const legacyMatch = /^Lv([1-6])$/i.exec(level);
    if (!legacyMatch) continue;
    const legacyPrefix = `Lv${legacyMatch[1]}`;
    for (const option of category.levels) {
      if (option.key === legacyPrefix || option.key.startsWith(`${legacyPrefix}_`)) {
        expanded.add(option.key);
      }
      if (legacyPrefix === 'Lv5' && (option.kind === 'goal' || option.key === 'proposal')) {
        expanded.add(option.key);
      }
      if (legacyPrefix === 'Lv6' && (option.kind === 'commitment' || option.key === 'acceptance_intent')) {
        expanded.add(option.key);
      }
    }
  }

  return category.levels
    .map((level) => level.key)
    .filter((level) => expanded.has(level));
}

export function InstructionFormEditor({
  data,
  onChange,
  disabled = false,
  categoryTable,
  conditionData,
}: InstructionFormEditorProps) {
  const [newDetail, setNewDetail] = useState<Record<number, string>>({});
  const categoryOptions = useMemo(
    () => buildDynamicCategoryOptions(conditionData),
    [conditionData],
  );

  const update = useCallback(
    (partial: Partial<InstructionFormData>) => {
      onChange({ ...data, ...partial });
    },
    [data, onChange]
  );

  const tasks = data.tasks ?? [];
  const checkedCategoryIds = new Set<string>(
    (data.target_categories && data.target_categories.length > 0
      ? data.target_categories
      : tasks.map((t) => (t.related_conditions ?? [])[0]).filter((c): c is string => !!c)
    ).map(canonicalTargetCategoryId)
  );

  // カテゴリ ON/OFF
  const toggleCategory = useCallback(
    (catId: string) => {
      const current = [...(data.tasks ?? [])];
      const indices = indicesByCategory(current, catId);
      const isCurrentlyChecked = (data.target_categories ?? []).map(canonicalTargetCategoryId).includes(catId) ||
        indices.length > 0;
      if (isCurrentlyChecked) {
        // OFF: そのカテゴリのタスクを全削除
        const nextTasks = renumberTasks(current.filter((t) => taskCategoryId(t) !== catId));
        update({
          tasks: nextTasks,
          target_categories: (data.target_categories ?? [])
            .map(canonicalTargetCategoryId)
            .filter((id) => id !== catId),
          target_category_levels: rebuildTargetCategoryLevels(nextTasks, categoryOptions, data.target_category_levels),
        });
      } else {
        // ON: 初回タスク（全Lv割当）を1件追加
        const category = categoryOptions.find((option) => option.id === catId);
        const levelKeys = category?.levels.map((level) => level.key) ?? LEGACY_LV_OPTIONS;
        const initial: InstructionTask = {
          no: current.length + 1,
          title: '',
          details: [],
          related_conditions: [catId],
          levels: [...levelKeys],
        };
        const nextTasks = renumberTasks([...current, initial]);
        update({
          tasks: nextTasks,
          target_categories: Array.from(new Set([...(data.target_categories ?? []).map(canonicalTargetCategoryId), catId])),
          target_category_levels: rebuildTargetCategoryLevels(nextTasks, categoryOptions, data.target_category_levels),
        });
      }
    },
    [categoryOptions, data.target_categories, data.target_category_levels, data.tasks, update]
  );

  // カテゴリ配下に新タスク追加
  const addTaskToCategory = useCallback(
    (catId: string) => {
      const current = [...(data.tasks ?? [])];
      const indices = indicesByCategory(current, catId);
      const maxTasks = categoryOptions.find((option) => option.id === catId)?.levels.length ?? 6;
      if (indices.length >= maxTasks) return;
      const nextTasks = renumberTasks([
        ...current,
        { no: current.length + 1, title: '', details: [], related_conditions: [catId], levels: [] },
      ]);
      update({
        tasks: nextTasks,
        target_categories: Array.from(new Set([...(data.target_categories ?? []).map(canonicalTargetCategoryId), catId])),
        target_category_levels: rebuildTargetCategoryLevels(nextTasks, categoryOptions, data.target_category_levels),
      });
    },
    [categoryOptions, data.target_categories, data.target_category_levels, data.tasks, update]
  );

  // タスク削除（indexベース）
  const removeTaskAt = useCallback(
    (index: number) => {
      const current = [...(data.tasks ?? [])];
      const remaining = renumberTasks(current.filter((_, i) => i !== index));
      update({
        tasks: remaining,
        target_categories: Array.from(new Set(remaining.map(taskCategoryId).filter(Boolean))) as string[],
        target_category_levels: rebuildTargetCategoryLevels(remaining, categoryOptions, data.target_category_levels),
      });
    },
    [categoryOptions, data.target_category_levels, data.tasks, update]
  );

  // 指定タスクのLv トグル。同一カテゴリ内の他タスクからは外す（排他）
  const toggleLevelOnTask = useCallback(
    (taskIndex: number, lv: string) => {
      const current = [...(data.tasks ?? [])];
      const t = current[taskIndex];
      if (!t) return;
      const catId = taskCategoryId(t);
      const category = categoryOptions.find((option) => option.id === catId);
      const fallbackLevels = category ? getTargetLevelsForCategory(data.target_category_levels, category) : undefined;
      const currentTaskLevels = t.levels ?? fallbackLevels ?? [];
      const hasLv = currentTaskLevels.includes(lv);
      const levelOrder = category?.levels.map((level) => level.key) ?? LEGACY_LV_OPTIONS;
      const updated = current.map((task, idx) => {
        const isSameCat = taskCategoryId(task) === catId;
        if (idx === taskIndex) {
          const nextLvs = hasLv
            ? currentTaskLevels.filter((l) => l !== lv)
            : levelOrder.filter((l) => new Set([...currentTaskLevels, lv]).has(l));
          return { ...task, levels: nextLvs };
        }
        if (isSameCat && !hasLv) {
          // 同一カテゴリの他タスクから対象Lvを外す（排他）
          return { ...task, levels: (task.levels ?? []).filter((l) => l !== lv) };
        }
        return task;
      });
      update({ tasks: updated, target_category_levels: rebuildTargetCategoryLevels(updated, categoryOptions, data.target_category_levels) });
    },
    [categoryOptions, data.target_category_levels, data.tasks, update]
  );

  // タスクのタイトル更新
  const updateTaskTitle = useCallback(
    (taskIndex: number, title: string) => {
      const current = [...(data.tasks ?? [])];
      if (!current[taskIndex]) return;
      current[taskIndex] = { ...current[taskIndex], title };
      update({ tasks: current });
    },
    [data.tasks, update]
  );

  // 確認項目 追加
  const addTaskDetail = useCallback(
    (taskIndex: number) => {
      const detail = newDetail[taskIndex]?.trim();
      if (!detail) return;
      const current = [...(data.tasks ?? [])];
      const t = current[taskIndex];
      if (!t) return;
      current[taskIndex] = { ...t, details: [...(t.details ?? []), detail] };
      update({ tasks: current });
      setNewDetail((prev) => ({ ...prev, [taskIndex]: '' }));
    },
    [data.tasks, newDetail, update]
  );

  // 確認項目 削除
  const removeTaskDetail = useCallback(
    (taskIndex: number, detailIndex: number) => {
      const current = [...(data.tasks ?? [])];
      const t = current[taskIndex];
      if (!t) return;
      current[taskIndex] = { ...t, details: (t.details ?? []).filter((_, i) => i !== detailIndex) };
      update({ tasks: current });
    },
    [data.tasks, update]
  );

  // 確認項目 編集
  const updateTaskDetail = useCallback(
    (taskIndex: number, detailIndex: number, value: string) => {
      const current = [...(data.tasks ?? [])];
      const t = current[taskIndex];
      if (!t) return;
      const details = [...(t.details ?? [])];
      details[detailIndex] = value;
      current[taskIndex] = { ...t, details };
      update({ tasks: current });
    },
    [data.tasks, update]
  );

  // タスク順序入れ替え
  const moveTask = useCallback(
    (index: number, direction: -1 | 1) => {
      const current = [...(data.tasks ?? [])];
      const target = index + direction;
      if (target < 0 || target >= current.length) return;
      const [moved] = current.splice(index, 1);
      current.splice(target, 0, moved);
      update({ tasks: renumberTasks(current) });
    },
    [data.tasks, update]
  );

  /** Lvボタンホバー時のツールチップ用: condition.categoryTable の該当セル値 */
  const levelTooltip = (catId: string, lv: string): string => {
    const dynamicTooltip = categoryOptions.find((category) => category.id === catId)
      ?.levels.find((level) => level.key === lv)?.tooltip;
    if (dynamicTooltip) return dynamicTooltip;
    if (!categoryTable) return '';
    const cell = categoryTable[catId] ?? categoryTable[`${catId}-T1`] ?? categoryTable[`${catId}-T0`];
    const val = cell?.[lv as keyof typeof cell];
    return typeof val === 'string' ? val : '';
  };

  const categoryLabelById = (id: string): string =>
    categoryOptions.find((c) => c.id === canonicalTargetCategoryId(id))?.label ?? id;

  const levelLabelByKey = (catId: string, lv: string): string =>
    categoryOptions.find((category) => category.id === catId)
      ?.levels.find((level) => level.key === lv)?.label ?? lv;

  return (
    <div className="space-y-6">
      {/* シナリオ概要 */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">シナリオ概要</CardTitle>
          <p className="text-xs text-muted-foreground">
            シナリオ選択カードに表示する1〜2文のネタバレなし要約。タスク詳細・カテゴリコードは含めない。
          </p>
        </CardHeader>
        <CardContent>
          <Textarea
            value={data.summary ?? ''}
            onChange={(e) => update({ summary: e.target.value })}
            placeholder="例: 行動変容が必要な患者へのアプローチ。ブラッシング習慣改善の提案が求められる。"
            rows={3}
            disabled={disabled}
          />
        </CardContent>
      </Card>

      {/* 対象カテゴリ・タスク */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">対象カテゴリ・タスク</CardTitle>
          <p className="text-xs text-muted-foreground">
            カテゴリをチェックすると配下にタスクを複数作成できます（最大6個、各タスクが担当するLvサブセットを持つ）。
            Lvボタンはマウスを当てると健康心理条件のセル内容が表示されます。
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {categoryOptions.map((cat) => {
            const checked = checkedCategoryIds.has(cat.id);
            const taskIndices = indicesByCategory(tasks, cat.id);
            return (
              <div key={cat.id} className="rounded border p-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleCategory(cat.id)}
                    disabled={disabled}
                    className="mt-1"
                  />
                  <span className="text-sm">
                    <span className="font-semibold mr-1">{cat.id}</span>
                    {cat.label}
                  </span>
                </label>

                {checked && (() => {
                  // このカテゴリ配下で既に使われている評価項目を集計
                  const usedLvs = new Set<string>();
                  taskIndices.forEach((i) => {
                    const levels = tasks[i]?.levels ?? getTargetLevelsForCategory(data.target_category_levels, cat) ?? [];
                    levels.forEach((lv) => usedLvs.add(lv));
                  });
                  const remainingLvCount = cat.levels.length - usedLvs.size;
                  return (
                  <div className="ml-6 mt-3 space-y-3">
                    {taskIndices.map((ti, nIdx) => {
                      const t = tasks[ti];
                      const selectedLvs = t.levels ?? getTargetLevelsForCategory(data.target_category_levels, cat) ?? [];
                      return (
                        <div key={ti} className="space-y-2 rounded bg-gray-50 p-3 border">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs font-semibold">
                              {cat.id} タスク {nIdx + 1}
                            </Label>
                            {!disabled && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs text-destructive"
                                onClick={() => removeTaskAt(ti)}
                                title="このタスクを削除"
                              >
                                <Trash2 className="w-3.5 h-3.5 mr-1" />
                                削除
                              </Button>
                            )}
                          </div>

                          {/* Lv選択（ホバーで条件セル内容） */}
                          <div>
                            <Label className="text-xs text-muted-foreground">評価レベル</Label>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {cat.levels.map((level) => {
                                const lv = level.key;
                                const on = selectedLvs.includes(lv);
                                const tooltip = levelTooltip(cat.id, lv);
                                const isBehavior = level.kind === 'goal' ||
                                  level.kind === 'commitment' ||
                                  lv === 'Lv5' ||
                                  lv === 'Lv6' ||
                                  lv === 'proposal' ||
                                  lv === 'acceptance_intent';
                                return (
                                  <button
                                    key={lv}
                                    type="button"
                                    onClick={() => toggleLevelOnTask(ti, lv)}
                                    disabled={disabled}
                                    title={tooltip || `${cat.sourceId}.${lv} の健康心理条件が未設定`}
                                    className={`px-2 py-1 rounded text-xs font-semibold border transition-colors ${
                                      on
                                        ? isBehavior
                                          ? 'bg-orange-100 text-orange-800 border-orange-300'
                                          : 'bg-blue-100 text-blue-800 border-blue-300'
                                        : 'bg-white text-gray-500 border-gray-200'
                                    }`}
                                  >
                                    {level.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* タスクタイトル */}
                          <div className="space-y-1">
                            <Label className="text-xs">タスクタイトル</Label>
                            <Input
                              value={t.title}
                              onChange={(e) => updateTaskTitle(ti, e.target.value)}
                              placeholder="例: セルフケアの方法について情報収集"
                              disabled={disabled}
                            />
                          </div>

                          {/* 確認項目 */}
                          <div className="space-y-1">
                            <Label className="text-xs">確認項目（詳細）</Label>
                            <div className="space-y-1">
                              {(t.details ?? []).map((d, di) => (
                                <div key={di} className="flex items-center gap-2">
                                  {disabled ? (
                                    <span className="flex-1 text-sm">{d}</span>
                                  ) : (
                                    <Input
                                      value={d}
                                      onChange={(e) => updateTaskDetail(ti, di, e.target.value)}
                                      className="flex-1"
                                    />
                                  )}
                                  {!disabled && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 text-xs"
                                      onClick={() => removeTaskDetail(ti, di)}
                                    >
                                      削除
                                    </Button>
                                  )}
                                </div>
                              ))}
                            </div>
                            {!disabled && (
                              <div className="flex gap-2">
                                <Input
                                  value={newDetail[ti] ?? ''}
                                  onChange={(e) =>
                                    setNewDetail((p) => ({ ...p, [ti]: e.target.value }))
                                  }
                                  placeholder="確認項目を追加"
                                  onKeyDown={(e) =>
                                    e.key === 'Enter' && (e.preventDefault(), addTaskDetail(ti))
                                  }
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => addTaskDetail(ti)}
                                >
                                  追加
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* タスク追加（残りLvが0なら無効化） */}
                    {!disabled && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full border-dashed"
                        onClick={() => addTaskToCategory(cat.id)}
                        disabled={remainingLvCount <= 0 || taskIndices.length >= cat.levels.length}
                        title={
                          remainingLvCount <= 0
                            ? '全Lvが既にタスクに割り当てられています'
                            : `${cat.id} にタスクを追加`
                        }
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {cat.id} にタスクを追加（残り {Math.max(0, remainingLvCount)}項目）
                      </Button>
                    )}
                  </div>
                  );
                })()}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* タスク順序プレビュー */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">タスク順序</CardTitle>
          <p className="text-xs text-muted-foreground">
            面接中の課題文に表示される順序です。↑↓ボタンで並び替えできます。
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {tasks.length === 0 && (
            <p className="text-sm text-muted-foreground">
              カテゴリを選択するとタスクが追加されます。
            </p>
          )}
          {tasks.map((t, idx) => {
            const catId = taskCategoryId(t);
            const lvs = (t.levels ?? []).map((lv) => levelLabelByKey(catId, lv)).join(',');
            return (
              <div
                key={idx}
                className="flex items-center gap-3 rounded border p-2 bg-white"
              >
                <span className="font-mono text-xs font-semibold text-muted-foreground w-8">
                  #{idx + 1}
                </span>
                <span className="font-mono text-xs text-gray-500 w-10">{catId}</span>
                <span className="text-[11px] text-gray-500 w-24 truncate" title={lvs}>
                  {lvs || '（Lv未選択）'}
                </span>
                <span className="text-xs text-gray-600 flex-shrink-0 truncate max-w-[120px]" title={categoryLabelById(catId)}>
                  {categoryLabelById(catId)}
                </span>
                <span className="text-sm flex-1 truncate" title={t.title}>
                  {t.title || <span className="text-gray-400">（タイトル未入力）</span>}
                </span>
                {!disabled && (
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => moveTask(idx, -1)}
                      disabled={idx === 0}
                      title="上へ"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => moveTask(idx, 1)}
                      disabled={idx === tasks.length - 1}
                      title="下へ"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => removeTaskAt(idx)}
                      title="このタスクを削除"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
